import React, { useState, useEffect, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ReferenceArea
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_CONFIG = {
  early:    { label: 'Early Phase',        color: '#FF9800', bg: 'rgba(255, 152, 0, 0.06)' },
  emerging: { label: 'Emerging Regulation', color: '#FFC107', bg: 'rgba(255, 193, 7, 0.06)' },
  growing:  { label: 'Growing Stability',   color: '#8BC34A', bg: 'rgba(139, 195, 74, 0.06)' },
  strong:   { label: 'Strong Regulation',   color: '#4CAF50', bg: 'rgba(76, 175, 80, 0.06)' },
};

const PERIOD_OPTIONS = [
  { days: 14, label: '14d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
];

const SUB_SCORES = [
  { key: 'regulation_capacity', label: 'Regulation', color: '#1E6B68' },
  { key: 'stability_score',     label: 'Stability',  color: '#2D8B87' },
  { key: 'practice_consistency', label: 'Consistency', color: '#4CAF50' },
];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const d = payload[0]?.payload;
  if (!d) return null;
  const band = BAND_CONFIG[d.interpretation_band] || BAND_CONFIG.early;

  return (
    <div data-testid="eri-chart-tooltip" style={{
      background: 'rgba(255,255,255,0.97)', borderRadius: '12px',
      padding: '14px 16px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
      border: '1px solid rgba(30,107,104,0.12)', minWidth: '160px'
    }}>
      <p style={{ margin: '0 0 8px', fontSize: '0.72rem', color: '#999' }}>{label}</p>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '6px' }}>
        <span style={{ fontSize: '1.4rem', fontWeight: '600', color: '#1E6B68' }}>
          {Math.round(d.eri_score)}
        </span>
        <span style={{ fontSize: '0.72rem', color: band.color, fontWeight: '500' }}>
          {band.label}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {SUB_SCORES.map(s => (
          <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem' }}>
            <span style={{ color: '#888' }}>{s.label}</span>
            <span style={{ color: s.color, fontWeight: '500' }}>{Math.round(d[s.key])}</span>
          </div>
        ))}
      </div>
      {d.entries_analyzed && (
        <p style={{ margin: '6px 0 0', fontSize: '0.68rem', color: '#bbb' }}>
          {d.entries_analyzed} sessions analyzed
        </p>
      )}
    </div>
  );
}

export default function ERITrendChart({ userId, demoData = null, onClose, isPro = false }) {
  const [scores, setScores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState(14); // free users start on 14d
  const [showSubScores, setShowSubScores] = useState(false);

  useEffect(() => {
    if (demoData) {
      setScores(demoData);
      setIsLoading(false);
      return;
    }
    if (userId) loadHistory();
  }, [userId, period, demoData]);

  const loadHistory = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/training/eri-history/${userId}?limit=${period}`);
      setScores(res.data.scores || []);
    } catch (e) {
      console.error('Failed to load ERI history:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const chartData = useMemo(() => {
    return [...scores].reverse().map(s => ({
      ...s,
      date: new Date(s.calculated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    }));
  }, [scores]);

  const latestScore = scores[0];
  const oldestScore = scores[scores.length - 1];
  const scoreDelta = latestScore && oldestScore
    ? Math.round(latestScore.eri_score - oldestScore.eri_score)
    : 0;

  const latestBand = latestScore ? (BAND_CONFIG[latestScore.interpretation_band] || BAND_CONFIG.early) : null;

  if (isLoading) {
    return (
      <div data-testid="eri-trend-loading" style={{
        background: 'rgba(255,255,255,0.95)', borderRadius: '20px',
        padding: '40px 20px', textAlign: 'center'
      }}>
        <div style={{
          width: '32px', height: '32px', borderRadius: '50%',
          border: '2px solid #B8D3CF', borderTopColor: '#1E6B68',
          animation: 'spin 0.8s linear infinite', margin: '0 auto 12px'
        }} />
        <p style={{ color: '#999', fontSize: '0.85rem' }}>Loading ERI trend...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (chartData.length < 2) {
    return (
      <div data-testid="eri-trend-empty" style={{
        background: 'rgba(255,255,255,0.95)', borderRadius: '20px',
        padding: '32px 24px', textAlign: 'center'
      }}>
        {onClose && (
          <button onClick={onClose} data-testid="eri-trend-close" style={{
            position: 'absolute', top: '16px', right: '16px',
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#999', fontSize: '1.2rem'
          }}>x</button>
        )}
        <div style={{
          width: '56px', height: '56px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #E8F5E9 0%, #B8D3CF 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px'
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1E6B68" strokeWidth="1.5">
            <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" />
          </svg>
        </div>
        <h3 style={{ color: '#1E6B68', fontSize: '1.05rem', margin: '0 0 8px', fontWeight: '500' }}>
          Building Your Trend
        </h3>
        <p style={{ color: '#888', fontSize: '0.85rem', margin: 0, lineHeight: '1.5' }}>
          Complete a few more practice sessions to unlock your ERI trend chart.
          At least 2 scored sessions are needed.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="eri-trend-chart" style={{
      background: 'rgba(255,255,255,0.95)', borderRadius: '20px',
      padding: '24px', position: 'relative',
      boxShadow: '0 2px 16px rgba(30,107,104,0.08)'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h3 data-testid="eri-trend-title" style={{
            margin: '0 0 4px', color: '#1E6B68',
            fontSize: '1rem', fontWeight: '500',
            fontFamily: "'Georgia', serif"
          }}>
            ERI Evolution
          </h3>
          <p style={{ margin: 0, color: '#999', fontSize: '0.75rem' }}>
            {chartData.length} data points
            {scoreDelta !== 0 && (
              <span style={{
                marginLeft: '8px',
                color: scoreDelta > 0 ? '#4CAF50' : '#FF9800',
                fontWeight: '500'
              }}>
                {scoreDelta > 0 ? '+' : ''}{scoreDelta} pts
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Period selector */}
          <div data-testid="eri-period-selector" style={{
            display: 'flex', background: '#f0f5f4', borderRadius: '10px', padding: '2px'
          }}>
            {PERIOD_OPTIONS.map(opt => {
              const locked = !isPro && opt.days > 14;
              return (
                <button
                  key={opt.days}
                  data-testid={`eri-period-${opt.days}`}
                  onClick={() => {
                    if (locked) {
                      import('sonner').then(({ toast }) =>
                        toast('Upgrade to Pro to view 30 and 90 day ERI trends', { duration: 3000 })
                      );
                      return;
                    }
                    setPeriod(opt.days);
                  }}
                  title={locked ? 'Pro feature' : undefined}
                  style={{
                    padding: '5px 12px', border: 'none',
                    cursor: locked ? 'default' : 'pointer',
                    borderRadius: '8px', fontSize: '0.72rem', fontWeight: '500',
                    background: period === opt.days ? '#1E6B68' : 'transparent',
                    color: period === opt.days ? '#fff' : locked ? '#ccc' : '#888',
                    transition: 'all 0.2s ease',
                    display: 'flex', alignItems: 'center', gap: '3px',
                  }}
                >
                  {locked && <span style={{ fontSize: '0.6rem' }}>🔒</span>}
                  {opt.label}
                </button>
              );
            })}
          </div>
          {onClose && (
            <button onClick={onClose} data-testid="eri-trend-close" style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#ccc', fontSize: '1.2rem', padding: '4px'
            }}>x</button>
          )}
        </div>
      </div>

      {/* Current Score Summary */}
      {latestScore && (
        <div data-testid="eri-latest-summary" style={{
          display: 'flex', alignItems: 'center', gap: '16px',
          marginBottom: '20px', padding: '14px 16px',
          background: `linear-gradient(135deg, ${latestBand.bg}, rgba(255,255,255,0.5))`,
          borderRadius: '14px', border: `1px solid ${latestBand.color}20`
        }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '50%',
            background: `conic-gradient(${latestBand.color} ${latestScore.eri_score}%, #f0f5f4 0%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <div style={{
              width: '42px', height: '42px', borderRadius: '50%', background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1E6B68' }}>
                {Math.round(latestScore.eri_score)}
              </span>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
              <span style={{ fontSize: '0.82rem', fontWeight: '500', color: latestBand.color }}>
                {latestBand.label}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '12px', fontSize: '0.72rem', color: '#888' }}>
              {SUB_SCORES.map(s => (
                <span key={s.key}>
                  <span style={{ color: s.color, fontWeight: '500' }}>{Math.round(latestScore[s.key])}</span>
                  {' '}{s.label.slice(0, 3)}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div data-testid="eri-chart-area" style={{ marginBottom: '16px' }}>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="eriGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1E6B68" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#1E6B68" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="regGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1E6B68" stopOpacity={0.08} />
                <stop offset="95%" stopColor="#1E6B68" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="stabGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2D8B87" stopOpacity={0.08} />
                <stop offset="95%" stopColor="#2D8B87" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="consGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.08} />
                <stop offset="95%" stopColor="#4CAF50" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(184,211,207,0.3)" vertical={false} />

            {/* Band zones */}
            <ReferenceArea y1={0} y2={39} fill="rgba(255,152,0,0.03)" />
            <ReferenceArea y1={40} y2={59} fill="rgba(255,193,7,0.03)" />
            <ReferenceArea y1={60} y2={79} fill="rgba(139,195,74,0.03)" />
            <ReferenceArea y1={80} y2={100} fill="rgba(76,175,80,0.03)" />

            {/* Band boundary lines */}
            <ReferenceLine y={40} stroke="#FFC107" strokeDasharray="4 4" strokeOpacity={0.35} />
            <ReferenceLine y={60} stroke="#8BC34A" strokeDasharray="4 4" strokeOpacity={0.35} />
            <ReferenceLine y={80} stroke="#4CAF50" strokeDasharray="4 4" strokeOpacity={0.35} />

            <XAxis
              dataKey="date" tick={{ fontSize: 10, fill: '#bbb' }}
              axisLine={false} tickLine={false}
              interval={Math.max(0, Math.floor(chartData.length / 6))}
            />
            <YAxis
              domain={[0, 100]} tick={{ fontSize: 10, fill: '#bbb' }}
              axisLine={false} tickLine={false}
              ticks={[0, 20, 40, 60, 80, 100]}
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Sub-score areas (behind main) */}
            {showSubScores && (
              <>
                <Area
                  type="monotone" dataKey="regulation_capacity"
                  stroke="#1E6B68" strokeWidth={1} strokeOpacity={0.4}
                  fill="url(#regGradient)" dot={false}
                  strokeDasharray="3 3"
                />
                <Area
                  type="monotone" dataKey="stability_score"
                  stroke="#2D8B87" strokeWidth={1} strokeOpacity={0.4}
                  fill="url(#stabGradient)" dot={false}
                  strokeDasharray="3 3"
                />
                <Area
                  type="monotone" dataKey="practice_consistency"
                  stroke="#4CAF50" strokeWidth={1} strokeOpacity={0.4}
                  fill="url(#consGradient)" dot={false}
                  strokeDasharray="3 3"
                />
              </>
            )}

            {/* Main ERI line */}
            <Area
              type="monotone" dataKey="eri_score"
              stroke="#1E6B68" strokeWidth={2.5}
              fill="url(#eriGradient)"
              dot={{ r: 3, fill: '#1E6B68', stroke: '#fff', strokeWidth: 2 }}
              activeDot={{ r: 5, fill: '#1E6B68', stroke: '#fff', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Controls */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderTop: '1px solid rgba(184,211,207,0.2)', paddingTop: '12px'
      }}>
        {/* Sub-scores toggle */}
        <button
          data-testid="eri-toggle-subscores"
          onClick={() => setShowSubScores(!showSubScores)}
          style={{
            background: showSubScores ? 'rgba(30,107,104,0.08)' : 'transparent',
            border: '1px solid rgba(30,107,104,0.15)', borderRadius: '8px',
            padding: '6px 12px', cursor: 'pointer', fontSize: '0.72rem',
            color: showSubScores ? '#1E6B68' : '#999', transition: 'all 0.2s',
            fontWeight: showSubScores ? '500' : '400'
          }}
        >
          {showSubScores ? 'Hide' : 'Show'} sub-scores
        </button>

        {/* Band legend */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {Object.entries(BAND_CONFIG).map(([key, cfg]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <div style={{
                width: '6px', height: '6px', borderRadius: '50%', background: cfg.color
              }} />
              <span style={{ fontSize: '0.62rem', color: '#bbb' }}>{cfg.label.split(' ')[0]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
